import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getData } from "../../../Services/Services";
import { getAssignedWaypoints } from "../../../Services/EndPoints";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
  Polyline,
  Circle,
  Polygon,
  TransitLayer,
} from "@react-google-maps/api";

// ─── Shared Google Maps loader ─────────────────────────────────────────────────
import {
  GOOGLE_MAPS_API_KEY,
  GOOGLE_MAPS_LIBRARIES,
  GOOGLE_MAPS_LOADER_ID,
} from "../../../../src/Pages/DMS/GpsTrackManagement/Googlemapsloader";

// ─── Map config ────────────────────────────────────────────────────────────────
const MAP_CONTAINER_STYLE = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 17.4435, lng: 78.3485 };
const MAP_OPTIONS = {
  mapTypeControl: false,
  streetViewControl: true,
  fullscreenControl: true,
  zoomControl: true,
};

const mapTypes = {
  road: "roadmap",
  satellite: "satellite",
  terrain: "terrain",
  hybrid: "hybrid",
};

// ─── Suppress native InfoWindow close button ───────────────────────────────────
const infoWindowStyle = `
  .gm-ui-hover-effect { display: none !important; }
  .gm-style-iw-chr   { display: none !important; }
  .gm-style-iw-d     { overflow: hidden !important; padding: 0 !important; }
  .gm-style-iw-c     { padding: 0 !important; border-radius: 8px !important;
                        box-shadow: 0 2px 10px rgba(0,0,0,0.2) !important; }
  .gm-style-iw       { padding: 0 !important; border-radius: 8px !important; }
`;

// ─── Layer options — inline SVGs ───────────────────────────────────────────────
const LAYERS = [
  {
    id: "road", label: "Standard",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/><line x1="9" y1="3" x2="9" y2="18"/><line x1="15" y1="6" x2="15" y2="21"/></svg>,
  },
  {
    id: "satellite", label: "Satellite",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
  },
  {
    id: "terrain", label: "Terrain",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 18 9 6 13 13 16 9 21 18"/></svg>,
  },
  {
    id: "hybrid", label: "Hybrid",
    icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  },
];

// ─── Inline RouteOff icon ──────────────────────────────────────────────────────
const RouteOffIcon = ({ size = 20, color = "red" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="19" r="2"/><circle cx="18" cy="5" r="2"/>
    <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-8a3.5 3.5 0 0 1 0-7H12"/>
    <line x1="2" y1="2" x2="22" y2="22"/>
  </svg>
);

// ─── Custom SVG pin icons ──────────────────────────────────────────────────────
const makePinSvg = (fillColor) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="44" viewBox="0 0 28 44">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 5.817 3.412 10.83 8.35 13.269L14 44l5.65-16.731C24.588 24.83 28 19.817 28 14 28 6.268 21.732 0 14 0z"
      fill="${fillColor}" stroke="white" stroke-width="1.5"/>
    <circle cx="14" cy="14" r="5.5" fill="white" fill-opacity="0.85"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const makeHaltSvg = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
    <circle cx="17" cy="17" r="16" fill="#FBBC04" stroke="white" stroke-width="2.5"/>
    <rect x="11" y="10" width="4" height="14" rx="1.5" fill="white"/>
    <rect x="19" y="10" width="4" height="14" rx="1.5" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const createCustomIcon = (type) => {
  if (!window.google) return undefined;
  const t = type?.toLowerCase();
  if (t === "halt") {
    return { url: makeHaltSvg(), scaledSize: new window.google.maps.Size(34, 34), anchor: new window.google.maps.Point(17, 17) };
  }
  const color = t === "start" ? "#34A853" : "#EA4335";
  return { url: makePinSvg(color), scaledSize: new window.google.maps.Size(28, 44), anchor: new window.google.maps.Point(14, 44) };
};

// ─── Geofence InfoWindow popup content ────────────────────────────────────────
const GeofencePopup = ({ gf, onClose }) => (
  <div style={{ padding: "10px 12px 12px", minWidth: 200 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
      <strong style={{ fontSize: 13, color: "#0f172a" }}>{gf.name}</strong>
      <button onClick={onClose} style={closeBtn}>✕</button>
    </div>
    <div style={{ fontSize: 12, color: "#555", lineHeight: 1.7 }}>
      <div><strong>Type:</strong> {gf.geometry?.type}</div>
      {gf.geometry?.type === "Circle" && (
        <div><strong>Radius:</strong> {gf.geometry.radius?.toFixed(1)} m</div>
      )}
      {gf.zone && <div><strong>Zone:</strong> {gf.zone}</div>}
      {gf.area > 0 && <div><strong>Area:</strong> {gf.area.toLocaleString()} m²</div>}
    </div>
  </div>
);

// ─── Component ─────────────────────────────────────────────────────────────────
const PlannedView = () => {
  const location = useLocation();
  const { data } = location.state || {};

  const { isLoaded } = useJsApiLoader({
    id: GOOGLE_MAPS_LOADER_ID,
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchTimerRef = useRef(null);

  const [vehicleTrackingData, setVehicleTrackingData] = useState([]);
  const [routeCoordinates, setRouteCoordinates] = useState([]);
  const [routePoints, setRoutePoints] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [keyPointsAddresses, setKeyPointsAddresses] = useState({});
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [geofenceInfoWindow, setGeofenceInfoWindow] = useState(null);

  // ── Layer switcher state ───────────────────────────────────────────────────
  const [selectedLayer, setSelectedLayer] = useState("road");
  const [showLayerControl, setShowLayerControl] = useState(false);
  const [showTransit, setShowTransit] = useState(false);
  const [isWaypointsExpanded, setIsWaypointsExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const geofences = data?.geofences || [];

  // ── Search helpers ─────────────────────────────────────────────────────────
  const searchLocation = useCallback((query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    if (!autocompleteRef.current) return;
    setIsSearching(true);
    autocompleteRef.current.getPlacePredictions(
      { input: query, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] },
      (predictions, status) => {
        setIsSearching(false);
        const OK = window.google.maps.places.PlacesServiceStatus.OK;
        if (status === OK && predictions?.length) {
          setSearchResults(predictions.map((p) => ({ id: p.place_id, name: p.description, placeId: p.place_id })));
        } else { setSearchResults([]); }
      }
    );
  }, []);

  const selectSearchResult = useCallback((result) => {
    setSearchQuery(result.name);
    setSearchResults([]);
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ placeId: result.placeId }, (results, status) => {
      if (status === "OK" && results[0]) {
        const loc = results[0].geometry.location;
        if (mapRef.current) {
          if (results[0].geometry.viewport) mapRef.current.fitBounds(results[0].geometry.viewport);
          else { mapRef.current.setCenter({ lat: loc.lat(), lng: loc.lng() }); mapRef.current.setZoom(15); }
        }
        toast.success(`Map centred on: ${result.name.split(",")[0]}`);
      }
    });
  }, []);

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => searchLocation(query), 500);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  };

  // ── Map ready ──────────────────────────────────────────────────────────────
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    geocoderRef.current = new window.google.maps.Geocoder();
    autocompleteRef.current = new window.google.maps.places.AutocompleteService();
  }, []);

  // ── Fit bounds ─────────────────────────────────────────────────────────────
  const fitMapToAll = useCallback((waypoints, gfList) => {
    if (!mapRef.current || !window.google) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    waypoints.forEach((p) => {
      bounds.extend({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) });
      hasPoints = true;
    });

    gfList.forEach((gf) => {
      const geo = gf.geometry;
      if (!geo) return;
      if (geo.type === "Circle" && geo.center) {
        bounds.extend({ lat: geo.center.lat, lng: geo.center.lng });
        hasPoints = true;
      } else if (geo.coordinates?.length) {
        const ring = Array.isArray(geo.coordinates[0][0]) ? geo.coordinates[0] : geo.coordinates;
        ring.forEach((c) => { bounds.extend({ lat: c[1], lng: c[0] }); hasPoints = true; });
      }
    });

    if (hasPoints) mapRef.current.fitBounds(bounds, { top: 60, right: 40, bottom: 40, left: 40 });
  }, []);

  // ── Reverse geocode ────────────────────────────────────────────────────────
  const reverseGeocode = useCallback((lat, lng) =>
    new Promise((resolve) => {
      if (!geocoderRef.current) { resolve(null); return; }
      geocoderRef.current.geocode(
        { location: { lat: parseFloat(lat), lng: parseFloat(lng) } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            const comps = results[0].address_components;
            const get = (...types) => {
              for (const t of types) { const c = comps.find((ac) => ac.types.includes(t)); if (c) return c.long_name; }
              return null;
            };
            const parts = [
              get("route", "premise", "establishment"),
              get("sublocality_level_1", "sublocality", "neighborhood"),
              get("locality", "administrative_area_level_3"),
              get("administrative_area_level_1"),
              get("postal_code"),
            ].filter(Boolean);
            resolve(parts.length ? parts.join(", ") : results[0].formatted_address);
          } else {
            resolve(null);
          }
        }
      );
    }), []);

  const geocodeKeyPoints = useCallback(
    async (keyPoints) => {
      const results = await Promise.allSettled(
        keyPoints.map(async (point, index) => {
          await new Promise((r) => setTimeout(r, index * 200));
          const address = await reverseGeocode(point.latitude, point.longitude);
          return { id: point.id, address: address || `${parseFloat(point.latitude).toFixed(6)}, ${parseFloat(point.longitude).toFixed(6)}` };
        })
      );
      const map = {};
      results.forEach((r) => { if (r.status === "fulfilled") map[r.value.id] = r.value.address; });
      setKeyPointsAddresses(map);
    }, [reverseGeocode]
  );

  // ── OSRM helpers ───────────────────────────────────────────────────────────
  const calcDist = (la1, ln1, la2, ln2) => {
    const R = 6371, dLat = (la2 - la1) * Math.PI / 180, dLng = (ln2 - ln1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(la1 * Math.PI / 180) * Math.cos(la2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const filterGPSPoints = (pts, minD = 0.005) => {
    if (pts.length <= 2) return pts;
    const out = [pts[0]];
    for (let i = 1; i < pts.length - 1; i++)
      if (calcDist(out[out.length - 1].lat, out[out.length - 1].lng, pts[i].lat, pts[i].lng) >= minD) out.push(pts[i]);
    out.push(pts[pts.length - 1]);
    return out;
  };

  const interpolateRoute = (pts) => {
    if (pts.length < 2) return pts.map((p) => ({ lat: p.lat, lng: p.lng }));
    const res = [];
    for (let i = 0; i < pts.length - 1; i++) {
      const s = pts[i], e = pts[i + 1];
      res.push({ lat: s.lat, lng: s.lng });
      for (let j = 1; j < 5; j++) res.push({ lat: s.lat + (e.lat - s.lat) * j / 5, lng: s.lng + (e.lng - s.lng) * j / 5 });
    }
    res.push({ lat: pts[pts.length - 1].lat, lng: pts[pts.length - 1].lng });
    return res;
  };

  const tryRoutingFallback = async (pts) => {
    try {
      const key = pts.length > 10 ? [pts[0], ...pts.slice(1, -1).filter((_, i) => i % Math.ceil(pts.length / 8) === 0), pts[pts.length - 1]] : pts;
      const coords = key.map((p) => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`).join(";");
      const res = await fetch(`https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=geojson`, { signal: AbortSignal.timeout(10000) });
      const d = await res.json();
      if (d.code === "Ok" && d.routes?.[0]?.geometry?.coordinates)
        return d.routes[0].geometry.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
      throw new Error("no route");
    } catch { return interpolateRoute(pts); }
  };

  const snapPointsToRoad = async (pts, maxRetries = 3) => {
    if (pts.length < 2) return pts.map((p) => ({ lat: p.lat, lng: p.lng }));
    const valid = pts.filter((p) => !isNaN(p.lat) && !isNaN(p.lng) && p.lat >= -90 && p.lat <= 90 && p.lng >= -180 && p.lng <= 180);
    if (valid.length < 2) return pts.map((p) => ({ lat: p.lat || 0, lng: p.lng || 0 }));
    const filtered = filterGPSPoints(valid, 0.005);

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const coords = filtered.map((p) => `${p.lng.toFixed(6)},${p.lat.toFixed(6)}`).join(";");
        const radiuses = filtered.map(() => "25").join(";");
        const ctrl = new AbortController();
        const tid = setTimeout(() => ctrl.abort(), 15000);
        const res = await fetch(`https://router.project-osrm.org/match/v1/driving/${coords}?overview=full&geometries=geojson&radiuses=${radiuses}&gaps=ignore&tidy=true`, { signal: ctrl.signal });
        clearTimeout(tid);
        if (!res.ok) throw new Error(`OSRM ${res.status}`);
        const d = await res.json();
        if (d.code === "Ok" && d.matchings?.[0]?.geometry?.coordinates?.length > 0) {
          const snapped = d.matchings[0].geometry.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
          if (snapped.length >= Math.max(2, filtered.length * 0.3)) return snapped;
        }
        if (d.code === "NoMatch" && attempt === maxRetries - 1) return tryRoutingFallback(filtered);
      } catch {
        if (attempt < maxRetries - 1) await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
      }
    }
    return tryRoutingFallback(filtered);
  };

  // ── Load waypoints ─────────────────────────────────────────────────────────
  const vehicleData = async (ID) => {
    setIsLoadingData(true);
    try {
      const responseData = await getData(getAssignedWaypoints.replace("ID", ID));
      setVehicleTrackingData(responseData);

      if (responseData.length > 1) {
        const validTypes = ["start", "halt", "end"];
        const allValid = responseData.every((p) => validTypes.includes(p.pointType?.toLowerCase()));
        const formatted = responseData.map((p) => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }));

        const snapped = allValid ? await snapPointsToRoad(formatted) : formatted;
        setRouteCoordinates(snapped);
        toast.info(allValid ? "Snapped route displayed" : "Full GPS route displayed", { autoClose: 2000 });

        const keyPoints = responseData.filter((p) => validTypes.includes(p.pointType?.toLowerCase()));
        setRoutePoints(keyPoints);
        setTimeout(() => fitMapToAll(responseData, geofences), 400);
        if (keyPoints.length > 0) geocodeKeyPoints(keyPoints);
      } else {
        setRouteCoordinates([]);
        setRoutePoints([]);
        if (geofences.length > 0) setTimeout(() => fitMapToAll([], geofences), 400);
      }
    } catch (err) {
      console.error("Failed to load vehicle data:", err);
      toast.error("Failed to load vehicle data");
      setVehicleTrackingData([]);
      setRouteCoordinates([]);
      setRoutePoints([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    if (data) vehicleData(data.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatTrackingTime = (ts) => {
    if (!ts) return "";
    const d = new Date(ts);
    const dd = String(d.getDate()).padStart(2, "0"), mm = String(d.getMonth() + 1).padStart(2, "0");
    let h = d.getHours(); const ampm = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
    return `${dd}-${mm}-${d.getFullYear()} ${String(h).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
  };

  const getMapCenter = () => {
    if (routeCoordinates.length) return routeCoordinates[0];
    if (vehicleTrackingData.length) return { lat: parseFloat(vehicleTrackingData[0].latitude), lng: parseFloat(vehicleTrackingData[0].longitude) };
    if (geofences.length) {
      const geo = geofences[0].geometry;
      if (geo?.type === "Circle" && geo.center) return { lat: geo.center.lat, lng: geo.center.lng };
      if (geo?.coordinates?.length) {
      const ring = Array.isArray(geo.coordinates[0][0]) ? geo.coordinates[0] : geo.coordinates;
      return { lat: ring[0][1], lng: ring[0][0] };
    }
    }
    return DEFAULT_CENTER;
  };

  const getHaltPoints = () => routePoints.filter((p) => p.pointType?.toLowerCase() === "halt");

  const polygonCentroid = (coordinates) => {
    const n = coordinates.length;
    return coordinates.reduce((acc, c) => ({ lat: acc.lat + c[1] / n, lng: acc.lng + c[0] / n }), { lat: 0, lng: 0 });
  };

  const openGeofenceInfo = (gf, anchor) => setGeofenceInfoWindow({ gf, anchor });
  const closeGeofenceInfo = () => setGeofenceInfoWindow(null);

  // ── Render geofences ───────────────────────────────────────────────────────
  const renderGeofences = () =>
    geofences.map((gf, idx) => {
      const geo = gf.geometry;
      const color = gf.color || "#1a7a73";
      const id = gf.id ?? idx;
      if (!geo) return null;

      if (geo.type === "Circle" && geo.center) {
        const anchor = { lat: geo.center.lat, lng: geo.center.lng };
        return (
          <Circle key={`gf-${id}`} center={anchor} radius={geo.radius}
            options={{ strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9, fillColor: color, fillOpacity: 0.2, cursor: "pointer" }}
            onClick={() => openGeofenceInfo(gf, anchor)} />
        );
      }
      if (geo.type === "Polygon" && geo.coordinates?.length) {
        const paths = geo.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
        const centroid = polygonCentroid(geo.coordinates);
        return (
          <Polygon key={`gf-${id}`} paths={paths}
            options={{ strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9, fillColor: color, fillOpacity: 0.2, cursor: "pointer" }}
            onClick={() => openGeofenceInfo(gf, centroid)} />
        );
      }
      if (geo.type === "LineString" && geo.coordinates?.length) {
        const path = geo.coordinates.map((c) => ({ lat: c[1], lng: c[0] }));
        const midPt = path[Math.floor(path.length / 2)];
        return (
          <Polyline key={`gf-${id}`} path={path}
            options={{ strokeColor: color, strokeWeight: 4, strokeOpacity: 0.85, cursor: "pointer" }}
            onClick={() => openGeofenceInfo(gf, midPt)} />
        );
      }
      // Administrative / Region geofences: no geometry type, nested coordinates [[[lng,lat],...]]
      if (geo.coordinates?.length) {
        const ring = Array.isArray(geo.coordinates[0][0]) ? geo.coordinates[0] : geo.coordinates;
        if (ring.length >= 3) {
          const paths = ring.map(([lng, lat]) => ({ lat, lng }));
          const centroid = paths.reduce((acc, p) => ({ lat: acc.lat + p.lat / paths.length, lng: acc.lng + p.lng / paths.length }), { lat: 0, lng: 0 });
          return (
            <Polygon key={`gf-${id}`} paths={paths}
              options={{ strokeColor: color, strokeWeight: 2, strokeOpacity: 0.9, fillColor: color, fillOpacity: 0.2, cursor: "pointer" }}
              onClick={() => openGeofenceInfo(gf, centroid)} />
          );
        }
      }
      return null;
    });

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{infoWindowStyle}</style>

      <div className="row" style={{ margin: 0, alignItems: "stretch" }}>
        {/* ── Left Panel ── */}
        <div className="col-lg-4" style={{ display: "flex", flexDirection: "column", padding: "14px 8px 14px 14px" }}>
      {/* ── Route Information ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, background: "white", borderRadius: 12, padding: "16px", boxShadow: "0 2px 10px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0" }}>

        {/* Section Header */}
        <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderLeft: "4px solid #0f4a45", borderRadius: 8, padding: "10px 14px", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0f4a45" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 5v4h-7V8z"/>
              <circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
            </svg>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#0f4a45", textTransform: "uppercase", letterSpacing: "0.4px" }}>Route Information</span>
          </div>
          <StatusBadge status={data?.status} />
        </div>

        {/* Info Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>

          <InfoCard
            accentColor="#1a7a73"
            iconBg="linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%)" iconColor="#1a7a73"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" /></svg>}
            label="Vehicle Number" value={data?.vehicleNumber}
          />

          <div style={{ ...cardStyle, borderLeft: "3px solid #0f4a45" }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
            <div style={{ width: 34, height: 34, borderRadius: 8, background: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)", color: "#0f4a45", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z" /></svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={labelStyle}>Route Name</div>
              <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 700, whiteSpace: "normal", wordBreak: "break-word", marginBottom: 3 }}>{data?.routeName}</div>
              <div style={{ fontSize: 11, display: "flex", flexWrap: "wrap", gap: "2px 12px" }}>
                <span><span style={{ color: "#059669", fontWeight: 600 }}>From: </span><span style={{ color: "#374151" }}>{data?.fromLocation || "N/A"}</span></span>
                <span><span style={{ color: "#dc2626", fontWeight: 600 }}>To: </span><span style={{ color: "#374151" }}>{data?.toLocation || "N/A"}</span></span>
              </div>
            </div>
          </div>

          <InfoCard
            accentColor="#059669"
            iconBg="linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)" iconColor="#059669"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
            label="Start Date & Time" value={data ? formatTrackingTime(data.startDate) : ""}
          />

          <InfoCard
            accentColor="#db2777"
            iconBg="linear-gradient(135deg, #fce7f3 0%, #fbcfe8 100%)" iconColor="#db2777"
            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            label="End Date & Time" value={data ? formatTrackingTime(data.endDate) : ""}
          />
        </div>

        {/* Cancelled info */}
        {data?.status === "CANCELLED" && (
          <div style={{ marginTop: 14, display: "flex", alignItems: "flex-start", gap: 14, padding: "14px 18px", background: "linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)", border: "1.5px solid #feb2b2", borderRadius: 10 }}>
            <div style={{ width: 44, height: 44, borderRadius: 10, background: "linear-gradient(135deg, #fed7d7 0%, #feb2b2 100%)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <RouteOffIcon size={20} color="#dc2626" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Trip Cancelled</div>
              <div style={{ fontSize: 13, color: "#0f172a", display: "flex", flexWrap: "wrap", gap: "4px 24px", lineHeight: 1.6 }}>
                <span><span style={{ color: "#dc2626", fontWeight: 600 }}>Reason: </span>{data?.reason}</span>
                <span><span style={{ color: "#dc2626", fontWeight: 600 }}>Comments: </span>{data?.comments}</span>
                <span><span style={{ color: "#dc2626", fontWeight: 600 }}>Cancelled On: </span>{data?.cancelledOn ? formatTrackingTime(data.cancelledOn) : "—"}</span>
              </div>
            </div>
          </div>
        )}

        {/* Route Waypoints — collapsible */}
        {routePoints.length > 0 && (
          <div style={{ marginTop: 12, background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            {/* Toggle header */}
            <div
              onClick={() => setIsWaypointsExpanded((p) => !p)}
              style={{ padding: "9px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none" }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#0f4a45" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  Route Waypoints
                </span>
                <span style={{ fontSize: 10, background: "#e2e8f0", color: "#64748b", padding: "1px 7px", borderRadius: 10, fontWeight: 600 }}>
                  {routePoints.length} points
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{isWaypointsExpanded ? "Collapse" : "Expand"}</span>
                <svg
                  width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: isWaypointsExpanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
                >
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </div>
            </div>

            {/* Collapsible content */}
            {isWaypointsExpanded && (
              <div style={{ borderTop: "1px solid #e2e8f0", padding: "12px 16px 14px" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 4, overflowX: "auto", paddingBottom: 4 }}>
                  {routePoints.map((point, idx) => {
                    const type = point.pointType?.toLowerCase();
                    const dotColor = type === "start" ? "#34A853" : type === "end" ? "#EA4335" : "#FBBC04";
                    const haltCount = routePoints.slice(0, idx).filter((p) => p.pointType?.toLowerCase() === "halt").length + 1;
                    const label = type === "start" ? "Start" : type === "end" ? "End" : `Halt ${haltCount}`;
                    const address = keyPointsAddresses[point.id];
                    return (
                      <React.Fragment key={point.id}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 90, maxWidth: 130 }}>
                          <div style={{ width: 30, height: 30, borderRadius: "50%", background: dotColor, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 5, flexShrink: 0, boxShadow: `0 2px 6px ${dotColor}66` }}>
                            <span style={{ color: "white", fontSize: 11, fontWeight: 700 }}>{type === "halt" ? "H" : type === "start" ? "S" : "E"}</span>
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 700, color: dotColor, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
                          {type === "halt" && point.waitTime && (
                            <div style={{ fontSize: 9, color: "#92400e", background: "#fef3c7", padding: "1px 6px", borderRadius: 4, marginBottom: 3, border: "1px solid #fde68a" }}>{point.waitTime} min</div>
                          )}
                          <div style={{ fontSize: 9, color: "#64748b", textAlign: "center", lineHeight: 1.3, maxWidth: 110 }}>
                            {address
                              ? address
                              : <span className="spinner-border spinner-border-sm" style={{ width: 8, height: 8, borderWidth: "1px" }} />}
                          </div>
                        </div>
                        {idx < routePoints.length - 1 && (
                          <div style={{ display: "flex", alignItems: "center", marginTop: 12, color: "#94a3b8", fontSize: 18, flexShrink: 0, padding: "0 4px" }}>›</div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

        </div>{/* end left col */}

        {/* ── Right Panel ── */}
        <div className="col-lg-8" style={{ display: "flex", flexDirection: "column", padding: "14px 14px 14px 8px" }}>
          <div style={{ flex: 1, borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.09)", display: "flex", flexDirection: "column" }}>
            {/* Gradient Map Card Header */}
            <div style={{ background: "linear-gradient(to right, #0f3d3a 0%, #0f4a45 50%, #1a7a73 100%)", padding: "5px 14px", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21"/>
                  <line x1="9" y1="3" x2="9" y2="18"/>
                  <line x1="15" y1="6" x2="15" y2="21"/>
                </svg>
              </div>
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 13, flex: 1 }}>Route Map</span>
              <span style={{ background: "rgba(255,255,255,0.18)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 20, padding: "2px 9px", fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                {(LAYERS.find(l => l.id === selectedLayer)?.label ?? selectedLayer)} layer
              </span>
            </div>
            {/* Search bar */}
            <div style={{ padding: "10px 14px 0", position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ padding: "0 10px", color: "#94a3b8", display: "flex", alignItems: "center" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                </div>
                <input type="text" placeholder="Search city, area, landmark or coordinates…"
                  value={searchQuery} onChange={handleSearchInputChange}
                  style={{ flex: 1, border: "none", outline: "none", fontSize: 13, padding: "8px 4px", color: "#1e293b", background: "transparent" }} />
                {searchQuery && (
                  <button type="button" onClick={clearSearch}
                    style={{ padding: "0 10px", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: 16, display: "flex", alignItems: "center" }}>✕</button>
                )}
                {isSearching && (
                  <div style={{ padding: "0 10px" }}>
                    <div className="spinner-border spinner-border-sm text-primary" role="status" style={{ width: 14, height: 14, borderWidth: 2 }} />
                  </div>
                )}
              </div>
              {searchResults.length > 0 && (
                <div style={{ position: "absolute", left: 14, right: 14, top: "calc(100% - 2px)", zIndex: 1050, background: "#fff", borderRadius: "0 0 10px 10px", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0", borderTop: "none", overflow: "hidden" }}>
                  {searchResults.map((result) => (
                    <button key={result.id} type="button" onClick={() => selectSearchResult(result)}
                      className="search-result-row"
                      style={{ width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid #f1f5f9", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "flex-start", gap: 10 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{result.name.split(",")[0]}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{result.name.split(",").slice(1, 3).join(",")}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {/* Map Body */}
            <div style={{ flex: 1, minHeight: 520, position: "relative", overflow: "hidden" }}>

          {isLoadingData && (
            <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(255,255,255,0.85)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
              <div className="spinner-border text-primary mb-3" role="status"><span className="visually-hidden">Loading...</span></div>
              <div style={{ fontSize: 14, color: "#666", fontWeight: 500 }}>Loading GPS data...</div>
            </div>
          )}

          {isLoaded ? (<>
            <GoogleMap
              mapContainerStyle={MAP_CONTAINER_STYLE}
              center={getMapCenter()}
              zoom={vehicleTrackingData.length > 0 ? 12 : 10}
              options={{ ...MAP_OPTIONS, mapTypeId: mapTypes[selectedLayer], minZoom: 4 }}
              onLoad={onMapLoad}
            >
              {!isLoadingData && (
                <>
                  {renderGeofences()}

                  {geofenceInfoWindow && (
                    <InfoWindow position={geofenceInfoWindow.anchor} onCloseClick={closeGeofenceInfo} options={{ maxWidth: 230 }}>
                      <GeofencePopup gf={geofenceInfoWindow.gf} onClose={closeGeofenceInfo} />
                    </InfoWindow>
                  )}

                  {routeCoordinates.length > 1 && (
                    <Polyline path={routeCoordinates} options={{ strokeColor: "#ff4444", strokeWeight: 4, strokeOpacity: 0.85 }} />
                  )}

                  {/* Start marker */}
                  {routePoints.length > 0 && (
                    <Marker
                      position={{ lat: parseFloat(routePoints[0].latitude), lng: parseFloat(routePoints[0].longitude) }}
                      icon={createCustomIcon("start")}
                      onClick={() => setActiveMarkerId(`start-${routePoints[0].id}`)}
                    >
                      {activeMarkerId === `start-${routePoints[0].id}` && (
                        <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 260 }}>
                          <MarkerPopup title="Start Point" titleColor="#22c55e" onClose={() => setActiveMarkerId(null)}
                            rows={[{ label: "Start Date & Time", value: formatTrackingTime(data?.startDate) }]}
                            address={keyPointsAddresses[routePoints[0].id]} />
                        </InfoWindow>
                      )}
                    </Marker>
                  )}

                  {/* Halt markers */}
                  {getHaltPoints().map((point, index) => (
                    <Marker key={point.id}
                      position={{ lat: parseFloat(point.latitude), lng: parseFloat(point.longitude) }}
                      icon={createCustomIcon("halt")}
                      onClick={() => setActiveMarkerId(`halt-${point.id}`)}
                    >
                      {activeMarkerId === `halt-${point.id}` && (
                        <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 260 }}>
                          <MarkerPopup title={`Halt Point ${index + 1}`} titleColor="#f59e0b" onClose={() => setActiveMarkerId(null)}
                            rows={[{ label: "Wait Time", value: `${point.waitTime} minutes` }]}
                            address={keyPointsAddresses[point.id]} />
                        </InfoWindow>
                      )}
                    </Marker>
                  ))}

                  {/* End marker */}
                  {routePoints.length > 1 && (
                    <Marker
                      position={{ lat: parseFloat(routePoints[routePoints.length - 1].latitude), lng: parseFloat(routePoints[routePoints.length - 1].longitude) }}
                      icon={createCustomIcon("end")}
                      onClick={() => setActiveMarkerId(`end-${routePoints[routePoints.length - 1].id}`)}
                    >
                      {activeMarkerId === `end-${routePoints[routePoints.length - 1].id}` && (
                        <InfoWindow onCloseClick={() => setActiveMarkerId(null)} options={{ maxWidth: 260 }}>
                          <MarkerPopup title="End Point" titleColor="#ef4444" onClose={() => setActiveMarkerId(null)}
                            rows={[{ label: "End Date & Time", value: formatTrackingTime(data?.endDate) }]}
                            address={keyPointsAddresses[routePoints[routePoints.length - 1].id]} />
                        </InfoWindow>
                      )}
                    </Marker>
                  )}

                </>
              )}
              {showTransit && <TransitLayer />}
            </GoogleMap>

            {/* ── Map Controls Column ── */}
            <div style={{ position: "absolute", top: 60, right: 10, zIndex: 1000, display: "flex", flexDirection: "column", gap: 8 }}>
              {/* Layer Switcher */}
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setShowLayerControl((p) => !p)}
                  style={{
                    width: 40, height: 40, borderRadius: 10,
                    background: showLayerControl ? "#1a7a73" : "#fff", border: `1px solid ${showLayerControl ? "#1a7a73" : "#e2e8f0"}`,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                    display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={showLayerControl ? "white" : "#475569"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                    <polyline points="2 17 12 22 22 17"/>
                    <polyline points="2 12 12 17 22 12"/>
                  </svg>
                </button>
                {showLayerControl && (
                  <div style={{
                    position: "absolute", top: 46, right: 0,
                    background: "#fff", borderRadius: 10, padding: 8,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.14)", minWidth: 150,
                    border: "1px solid #e2e8f0",
                  }}>
                    {LAYERS.map((l) => (
                      <div key={l.id}
                        onClick={() => {
                          setSelectedLayer(l.id);
                          mapRef.current?.setMapTypeId(mapTypes[l.id]);
                          setShowLayerControl(false);
                        }}
                        style={{
                          display: "flex", alignItems: "center", gap: 9,
                          padding: "8px 10px", borderRadius: 7, cursor: "pointer",
                          background: selectedLayer === l.id ? "#f0fdfb" : "transparent",
                          transition: "background 0.15s",
                        }}
                      >
                        <span style={{ color: selectedLayer === l.id ? "#1a7a73" : "#94a3b8" }}>{l.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: selectedLayer === l.id ? 600 : 400, color: selectedLayer === l.id ? "#1a7a73" : "#334155" }}>
                          {l.label}
                        </span>
                        {selectedLayer === l.id && (
                          <span style={{ marginLeft: "auto", width: 7, height: 7, borderRadius: "50%", background: "#1a7a73" }} />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transit Layer Toggle */}
              <button
                onClick={() => setShowTransit((p) => !p)}
                title={showTransit ? "Hide transit" : "Show transit"}
                style={{ width: 40, height: 40, borderRadius: 10, background: showTransit ? "#1a7a73" : "#fff", border: "1px solid #e2e8f0", boxShadow: "0 2px 8px rgba(0,0,0,0.12)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={showTransit ? "#fff" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="2" width="16" height="16" rx="2"/>
                  <path d="M4 10h16"/><path d="M8 2v8"/><path d="M16 2v8"/>
                  <path d="M8 18l-2 4"/><path d="M16 18l2 4"/>
                </svg>
              </button>
            </div>
          </>) : (
            <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc" }}>
              <div className="spinner-border text-info" role="status"><span className="visually-hidden">Loading map…</span></div>
            </div>
          )}
            </div>{/* end Map Body */}
          </div>{/* end Map Card */}
          {/* Legend bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "8px 4px", flexWrap: "wrap" }}>
            {[
              { color: "#34A853", label: "Start" },
              { color: "#FBBC04", label: "Halt" },
              { color: "#EA4335", label: "End" },
              { color: "#ff4444", label: "Route", isLine: true },
            ].map((item) => (
              <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {item.isLine
                  ? <div style={{ width: 18, height: 3, background: item.color, borderRadius: 2 }} />
                  : <div style={{ width: 10, height: 10, borderRadius: "50%", background: item.color }} />}
                <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>{/* end right col */}
      </div>{/* end row */}
    </>
  );
};

// ── Shared micro-components & styles ──────────────────────────────────────────
const MarkerPopup = ({ title, titleColor, onClose, rows, address }) => {
  const isHalt  = title.toLowerCase().includes("halt");
  const isStart = title.toLowerCase().includes("start");
  const isEnd   = title.toLowerCase().includes("end");
  const gradMap = {
    "#22c55e": "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    "#f59e0b": "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    "#ef4444": "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
  };
  const tintMap = {
    "#22c55e": "#f0fdf4",
    "#f59e0b": "#fffbeb",
    "#ef4444": "#fef2f2",
  };
  const hdrGrad = gradMap[titleColor] || `linear-gradient(135deg, ${titleColor} 0%, ${titleColor} 100%)`;
  const tintBg  = tintMap[titleColor] || "#f8fafc";
  const subtitle = isStart ? "Planned starting location" : isEnd ? "Planned ending location" : "Scheduled stop location";
  return (
    <div style={{ minWidth: 260, maxWidth: 300, overflow: "hidden", fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ background: hdrGrad, padding: "12px 14px 10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {isHalt
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16" rx="2"/><rect x="14" y="4" width="4" height="16" rx="2"/></svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5" fill="white" stroke="none"/></svg>
          }
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{title}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.82)", marginTop: 1 }}>{subtitle}</div>
        </div>
        <button onClick={onClose} style={{ width: 22, height: 22, borderRadius: "50%", background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.35)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, padding: 0, flexShrink: 0 }}>✕</button>
      </div>
      <div style={{ padding: "12px 14px 14px", background: "#fff" }}>
        {rows.map((r, i) => (
          <div key={r.label} style={{ display: "flex", alignItems: "flex-start", gap: 9, marginBottom: 10 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: tintBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={titleColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>{r.label}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{r.value || "—"}</div>
            </div>
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 9 }}>
          <div style={{ width: 26, height: 26, borderRadius: 6, background: tintBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={titleColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Location</div>
            <div style={{ fontSize: 11, color: "#475569", background: tintBg, borderRadius: 6, padding: "6px 10px", borderLeft: `3px solid ${titleColor}`, lineHeight: 1.5 }}>
              {address
                ? address
                : <><span className="spinner-border spinner-border-sm me-2" style={{ width: 10, height: 10 }} />Loading address…</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const configs = {
    "IN PROGRESS":              { text: "IN PROGRESS",      bg: "#fef3c7", color: "#d97706" },
    "TRAVELED":                 { text: "TRAVELED",          bg: "#dcfce7", color: "#16a34a" },
    "NOT TRAVELED":             { text: "NOT TRAVELED",      bg: "#fee2e2", color: "#dc2626" },
    "NOT YET TRAVELED":         { text: "NOT YET TRAVELED", bg: "#ccfbf1", color: "#0f4a45" },
    "CANCELLED":                { text: "CANCELLED",         bg: "#fee2e2", color: "#dc2626" },
    "TRIP_TERMINATED_INCIDENT": { text: "TRIP TERMINATED",  bg: "#fee2e2", color: "#dc2626" },
    "GPS_UNAVAILABLE":          { text: "GPS UNAVAILABLE",  bg: "#fef3c7", color: "#d97706" },
  };
  const cfg = configs[status] || { text: status, bg: "#ccfbf1", color: "#0f4a45" };
  return (
    <span style={{ background: cfg.bg, color: cfg.color, padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: "0.3px", whiteSpace: "nowrap" }}>
      {cfg.text}
    </span>
  );
};

const cardStyle = {
  display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
  background: "#ffffff", border: "1px solid #e8ecf4", borderRadius: 8,
  transition: "all 0.2s ease", cursor: "default",
};
const hoverIn  = (e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.09)"; e.currentTarget.style.borderColor = "#c7d7f0"; };
const hoverOut = (e) => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "none";                          e.currentTarget.style.borderColor = "#e8ecf4"; };
const labelStyle = { fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 2 };
const closeBtn   = { background: "none", border: "none", cursor: "pointer", fontSize: 15, color: "#dc3545", fontWeight: "bold", padding: "0 0 0 8px", outline: "none" };

const InfoCard = ({ accentColor = "#0f4a45", iconBg, iconColor, icon, label, value }) => (
  <div style={{ ...cardStyle, borderLeft: `3px solid ${accentColor}` }} onMouseEnter={hoverIn} onMouseLeave={hoverOut}>
    <div style={{ width: 34, height: 34, borderRadius: 8, background: iconBg, color: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value || "—"}</div>
    </div>
  </div>
);

export default PlannedView;